import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Input, Select,
  Space, Descriptions, message, Avatar, Spin, Tabs
} from 'antd';
import {
  EyeOutlined, StopOutlined, CheckCircleOutlined,
  ReloadOutlined, HomeOutlined, ToolOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc, where
} from 'firebase/firestore';
import { homeownerDb, db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;
const { Search } = Input;

const JOB_STATUS = {
  pending:   { color: '#FAAD14', label: 'Pending' },
  active:    { color: '#1890FF', label: 'Active' },
  completed: { color: '#52C41A', label: 'Completed' },
  cancelled: { color: '#FF4D4F', label: 'Cancelled' },
};

export default function HomeOwnerManagement() {
  const [homeowners, setHomeowners]         = useState([]);
  const [jobs, setJobs]                     = useState([]);
  const [selected, setSelected]             = useState(null);
  const [selectedJobs, setSelectedJobs]     = useState([]);
  const [viewModal, setViewModal]           = useState(false);
  const [actionModal, setActionModal]       = useState(false);
  const [actionType, setActionType]         = useState('');
  const [searchText, setSearchText]         = useState('');
  const [jobSearchText, setJobSearchText]   = useState('');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [filterJobStatus, setFilterJobStatus] = useState('all');
  const [loading, setLoading]               = useState(true);
  const [jobsLoading, setJobsLoading]       = useState(false);
  const [actionLoading, setActionLoading]   = useState(false);
  const [activeTab, setActiveTab]           = useState('1');
  const [modalTab, setModalTab]             = useState('1');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const usersSnap = await getDocs(
        query(collection(homeownerDb, 'users'), orderBy('createdAt', 'desc'))
      );
      const usersData = usersSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      }));
      setHomeowners(usersData);

      const jobsSnap = await getDocs(
        query(collection(homeownerDb, 'jobs'), orderBy('createdAt', 'desc'))
      );
      const jobsData = jobsSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      }));
      setJobs(jobsData);
    } catch (error) {
      message.error('Failed to load HomeOwner data: ' + error.message);
    }
    setLoading(false);
  };

  const loadUserJobs = async (userId) => {
    setJobsLoading(true);
    try {
      const q = query(
        collection(homeownerDb, 'jobs'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setSelectedJobs(snap.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      })));
    } catch { setSelectedJobs([]); }
    setJobsLoading(false);
  };

  const handleViewUser = (user) => {
    setSelected(user);
    setViewModal(true);
    setModalTab('1');
    loadUserJobs(user.id);
  };

  const stats = {
    total:         homeowners.length,
    active:        homeowners.filter(u => u.status !== 'suspended').length,
    suspended:     homeowners.filter(u => u.status === 'suspended').length,
    totalJobs:     jobs.length,
    pendingJobs:   jobs.filter(j => j.status === 'pending').length,
    completedJobs: jobs.filter(j => j.status === 'completed').length,
  };

  const filteredUsers = homeowners.filter(u => {
    const matchSearch = !searchText ||
      u.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      u.phone?.includes(searchText) ||
      u.email?.toLowerCase().includes(searchText.toLowerCase());
    const matchStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const filteredJobs = jobs.filter(j => {
    const user = homeowners.find(u => u.id === j.userId);
    const matchSearch = !jobSearchText ||
      j.workCategory?.toLowerCase().includes(jobSearchText.toLowerCase()) ||
      j.specificWork?.toLowerCase().includes(jobSearchText.toLowerCase()) ||
      j.city?.toLowerCase().includes(jobSearchText.toLowerCase()) ||
      user?.name?.toLowerCase().includes(jobSearchText.toLowerCase());
    const matchStatus = filterJobStatus === 'all' || j.status === filterJobStatus;
    return matchSearch && matchStatus;
  });

  const handleAction = async () => {
    setActionLoading(true);
    try {
      const newStatus = actionType === 'suspend' ? 'suspended' : 'active';
      await updateDoc(doc(homeownerDb, 'users', selected.id), {
        status: newStatus, updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'HOMEOWNER ' + (actionType === 'suspend' ? 'SUSPENDED' : 'REACTIVATED'),
        details: 'HomeOwner: ' + selected.name + ' | Phone: ' + selected.phone,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'homeowner',
        timestamp: serverTimestamp(),
      });
      setHomeowners(prev =>
        prev.map(u => u.id === selected.id ? { ...u, status: newStatus } : u)
      );
      message.success('HomeOwner ' + (actionType === 'suspend' ? 'suspended' : 'reactivated'));
      setActionModal(false);
      setViewModal(false);
    } catch (error) {
      message.error('Action failed: ' + error.message);
    }
    setActionLoading(false);
  };

  const userColumns = [
    {
      title: 'HomeOwner',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar style={{ background: '#1890FF30', color: '#1890FF', flexShrink: 0 }} size={36}>
            {r.name?.charAt(0) || 'H'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.name || 'N/A'}</div>
            <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.phone} • {r.city || 'N/A'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: e => <span style={{ color: '#7A8A96', fontSize: 12 }}>{e || '—'}</span>,
    },
    {
      title: 'Jobs Posted',
      render: (_, r) => {
        const count = jobs.filter(j => j.userId === r.id).length;
        return (
          <Tag style={{ background: '#1890FF20', color: '#1890FF', border: 'none', borderRadius: 20 }}>
            {count} Jobs
          </Tag>
        );
      },
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      render: d => <span style={{ color: '#7A8A96', fontSize: 12 }}>{d}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: status => (
        <Badge status={status === 'suspended' ? 'error' : 'success'}
          text={<span style={{ color: status === 'suspended' ? '#FF4D4F' : '#52C41A', fontSize: 12 }}>
            {status === 'suspended' ? 'Suspended' : 'Active'}
          </span>} />
      ),
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => handleViewUser(r)} />
          {r.status !== 'suspended' ? (
            <Button type="text" icon={<StopOutlined />} style={{ color: '#FF4D4F' }}
              onClick={() => { setSelected(r); setActionType('suspend'); setActionModal(true); }} />
          ) : (
            <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
              onClick={() => { setSelected(r); setActionType('reactivate'); setActionModal(true); }} />
          )}
        </Space>
      ),
    },
  ];

  const jobColumns = [
    {
      title: 'Job',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#D4DCE3', fontSize: 13 }}>{r.workCategory || 'N/A'}</div>
          <div style={{ fontSize: 11, color: '#C9A84C' }}>{r.specificWork || '—'}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.city || 'N/A'} • {r.createdAt}</div>
        </div>
      ),
    },
    {
      title: 'Property',
      render: (_, r) => (
        <div>
          <div style={{ color: '#B0B8C1', fontSize: 12 }}>{r.propertyType || '—'}</div>
          <div style={{ color: '#7A8A96', fontSize: 11 }}>{r.areaSize || '—'}</div>
        </div>
      ),
    },
    {
      title: 'Timeline',
      render: (_, r) => (
        <div>
          <div style={{ color: '#B0B8C1', fontSize: 12 }}>{r.timeline || '—'}</div>
          <div style={{ color: '#7A8A96', fontSize: 11 }}>{r.callTime || '—'}</div>
        </div>
      ),
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      render: b => <span style={{ color: '#C9A84C', fontSize: 12 }}>{b || '—'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: status => {
        const s = JOB_STATUS[status] || { color: '#7A8A96', label: status || 'Unknown' };
        return (
          <Tag style={{ background: s.color + '20', color: s.color, border: 'none', borderRadius: 20, fontSize: 11 }}>
            {s.label}
          </Tag>
        );
      },
    },
  ];

  // ── Main Tabs ──
  const mainTabItems = [
    {
      key: '1',
      label: <span style={{ color: '#C9A84C' }}>👤 HomeOwners ({homeowners.length})</span>,
      children: (
        <div style={{ padding: '12px 0' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Search placeholder="Search name, phone, email..."
              value={searchText} onChange={e => setSearchText(e.target.value)} style={{ width: 260 }} />
            <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 140 }}>
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
            <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }}
              onClick={loadData}>Refresh</Button>
            <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12, alignSelf: 'center' }}>
              {filteredUsers.length} homeowners
            </span>
          </div>
          <Spin spinning={loading}>
            <Table dataSource={filteredUsers} columns={userColumns}
              rowKey="id" pagination={{ pageSize: 10 }} />
          </Spin>
        </div>
      ),
    },
    {
      key: '2',
      label: <span style={{ color: '#C9A84C' }}>🔨 All Jobs ({jobs.length})</span>,
      children: (
        <div style={{ padding: '12px 0' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Search placeholder="Search category, city, homeowner..."
              value={jobSearchText} onChange={e => setJobSearchText(e.target.value)} style={{ width: 260 }} />
            <Select value={filterJobStatus} onChange={setFilterJobStatus} style={{ width: 140 }}>
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="active">Active</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
            <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }}
              onClick={loadData}>Refresh</Button>
            <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12, alignSelf: 'center' }}>
              {filteredJobs.length} jobs
            </span>
          </div>
          <Spin spinning={loading}>
            <Table
              dataSource={filteredJobs}
              columns={[
                {
                  title: 'HomeOwner',
                  render: (_, r) => {
                    const user = homeowners.find(u => u.id === r.userId);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar style={{ background: '#1890FF30', color: '#1890FF' }} size={30}>
                          {user?.name?.charAt(0) || '?'}
                        </Avatar>
                        <div>
                          <div style={{ color: '#D4DCE3', fontSize: 13 }}>{user?.name || 'Unknown'}</div>
                          <div style={{ color: '#7A8A96', fontSize: 11 }}>{user?.phone || '—'}</div>
                        </div>
                      </div>
                    );
                  },
                },
                ...jobColumns,
              ]}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Spin>
        </div>
      ),
    },
  ];

  // ── Modal Tabs ──
  const modalTabItems = selected ? [
    {
      key: '1',
      label: 'Profile',
      children: (
        <Descriptions column={2} bordered size="small" style={{ marginTop: 12 }}>
          <Descriptions.Item label="Name">{selected.name}</Descriptions.Item>
          <Descriptions.Item label="Phone">{selected.phone}</Descriptions.Item>
          <Descriptions.Item label="Email" span={2}>{selected.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="City">{selected.city || '—'}</Descriptions.Item>
          <Descriptions.Item label="District">{selected.district || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Badge status={selected.status === 'suspended' ? 'error' : 'success'}
              text={selected.status === 'suspended' ? 'Suspended' : 'Active'} />
          </Descriptions.Item>
          <Descriptions.Item label="Joined">{selected.createdAt}</Descriptions.Item>
          <Descriptions.Item label="WhatsApp">{selected.whatsapp || '—'}</Descriptions.Item>
          <Descriptions.Item label="Jobs Posted" span={2}>
            {jobs.filter(j => j.userId === selected.id).length}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: '2',
      label: `Jobs (${selectedJobs.length})`,
      children: (
        <Spin spinning={jobsLoading}>
          <Table dataSource={selectedJobs} columns={jobColumns}
            rowKey="id" pagination={{ pageSize: 5 }} size="small"
            style={{ marginTop: 12 }} />
        </Spin>
      ),
    },
  ] : [];

  return (
    <div>
      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total HomeOwners', value: stats.total,         color: '#1890FF', icon: <HomeOutlined /> },
          { title: 'Active',           value: stats.active,        color: '#52C41A', icon: <CheckCircleOutlined /> },
          { title: 'Suspended',        value: stats.suspended,     color: '#FF4D4F', icon: <StopOutlined /> },
          { title: 'Total Jobs',       value: stats.totalJobs,     color: '#C9A84C', icon: <ToolOutlined /> },
          { title: 'Pending Jobs',     value: stats.pendingJobs,   color: '#FAAD14', icon: <ToolOutlined /> },
          { title: 'Completed Jobs',   value: stats.completedJobs, color: '#52C41A', icon: <ToolOutlined /> },
        ].map((stat, i) => (
          <Col xs={12} lg={4} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic
                title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value}
                valueStyle={{ color: stat.color, fontSize: 24, fontWeight: 600 }}
                prefix={<span style={{ color: stat.color, marginRight: 6, fontSize: 16 }}>{stat.icon}</span>}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Tabs */}
      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={mainTabItems}
          style={{ padding: '0 16px' }}
          tabBarStyle={{ borderBottom: '1px solid rgba(201,168,76,0.15)' }}
        />
      </Card>

      {/* View User Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>👤 {selected?.name}</span>}
        open={viewModal} onCancel={() => setViewModal(false)} width={700}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            {selected?.status !== 'suspended' ? (
              <Button danger icon={<StopOutlined />}
                onClick={() => { setActionType('suspend'); setActionModal(true); }}>
                Suspend
              </Button>
            ) : (
              <Button icon={<CheckCircleOutlined />}
                style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => { setActionType('reactivate'); setActionModal(true); }}>
                Reactivate
              </Button>
            )}
          </div>
        }>
        <Tabs activeKey={modalTab} onChange={setModalTab} items={modalTabItems} />
      </Modal>

      {/* Action Modal */}
      <Modal
        title={
          <span style={{ color: actionType === 'suspend' ? '#FF4D4F' : '#52C41A' }}>
            {actionType === 'suspend' ? 'Suspend' : 'Reactivate'} — {selected?.name}
          </span>
        }
        open={actionModal} onCancel={() => setActionModal(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setActionModal(false)}>Cancel</Button>
            <Button loading={actionLoading} danger={actionType === 'suspend'}
              style={actionType !== 'suspend' ? { background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 } : {}}
              onClick={handleAction}>
              Confirm {actionType === 'suspend' ? 'Suspension' : 'Reactivation'}
            </Button>
          </div>
        }>
        <div style={{ padding: '16px 0', color: '#B0B8C1', fontSize: 13 }}>
          {actionType === 'suspend'
            ? 'HomeOwner will be blocked immediately. Change saved to Firebase.'
            : 'HomeOwner access will be restored. Change saved to Firebase.'}
        </div>
        <div style={{ padding: '12px 16px', background: 'rgba(201,168,76,0.08)', border: '0.5px solid rgba(201,168,76,0.2)', borderRadius: 8 }}>
          <div style={{ color: '#D4DCE3', fontWeight: 500 }}>{selected?.name}</div>
          <div style={{ color: '#7A8A96', fontSize: 12, marginTop: 4 }}>
            HomeOwner • {selected?.city} • {selected?.phone}
          </div>
        </div>
      </Modal>
    </div>
  );
}